import { createFileRoute, Link, Navigate, notFound, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { ConfirmEnrolment } from "@/features/seat-orders/components/ConfirmEnrolment";
import {
  clearRedeemCode,
  readRedeemCode,
  storeRedeemCode,
} from "@/features/seat-orders/redeemCode";
import { fetchFeatureFlagsIfAvailable } from "@/hooks/useFeatureFlags";
import { useAuthStore } from "@/stores/authStore";

export const Route = createFileRoute("/redeem")({
  validateSearch: (search: Record<string, unknown>): { code?: string } => ({
    code: typeof search.code === "string" && search.code ? search.code : undefined,
  }),
  beforeLoad: async ({ search }) => {
    // Keep the invite code and take it out of the address bar before anything else runs, so it never reaches
    // analytics, logs, a Referer or the login redirect.
    if (search.code) {
      storeRedeemCode(search.code);
      throw redirect({ to: "/redeem", search: {}, replace: true });
    }

    // Settle who is signed in first, so the flags below are the participant's own.
    const signedIn = await resolveSignedIn();

    // Seat orders are hidden while the payments or seat-orders flag is off (the API 404s redeeming too). Forget the
    // code then, so signing in later doesn't come back here (LMS-188); but keep it when the flags couldn't be read, so
    // trying again resumes the invite.
    const flags = await fetchFeatureFlagsIfAvailable();
    if (!flags?.payments || !flags.seatOrders) {
      if (flags) clearRedeemCode();
      throw notFound();
    }

    // A signed-out participant signs in first and comes back here; the code waits in session storage.
    if (!signedIn) {
      throw redirect({ to: "/login", search: { redirect: "/redeem" }, replace: true });
    }
  },
  notFoundComponent: RedeemNotFound,
  component: RedeemPage,
});

/**
 * Whether this tab is signed in, and as whom, from the session cookie rather than what the tab kept. Until this page
 * load has asked the API, it waits for the answer: a fresh tab keeps no auth state though the cookie may be valid, and
 * a kept account may be stale (the cookie is shared by every tab, so another tab may have signed in as someone else),
 * while Confirm enrolment must name the account the place will go to.
 */
async function resolveSignedIn(): Promise<boolean> {
  const { isInitialized, checkAuth } = useAuthStore.getState();
  if (!isInitialized) {
    // checkAuth returns at once while AuthProvider's own check is in flight, so wait for that to finish too.
    await checkAuth();
    if (useAuthStore.getState().isLoading) {
      await new Promise<void>((resolve) => {
        const unsubscribe = useAuthStore.subscribe((state) => {
          if (!state.isLoading) {
            unsubscribe();
            resolve();
          }
        });
      });
    }
  }
  return useAuthStore.getState().isAuthenticated;
}

/**
 * RedeemPage - Where a seat invite link leads (LMS-150): Confirm enrolment for the signed-in participant, for the code
 * the link carried.
 */
function RedeemPage() {
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  // Read once: confirming or declining clears the stored code, but this page keeps showing its outcome.
  const [code] = useState(readRedeemCode);
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ");

  // The session ended (the API answered 401 and the auth store was cleared): sign in again and come back.
  if (!isAuthenticated) {
    return <Navigate to="/login" search={{ redirect: "/redeem" }} replace />;
  }

  return (
    <div className="bg-arc-gray-50 flex min-h-screen items-center justify-center px-4 py-16">
      <ConfirmEnrolment code={code} account={{ name, email: user?.email ?? "" }} />
    </div>
  );
}

function RedeemNotFound() {
  return (
    <section
      aria-labelledby="redeem-not-found-heading"
      className="bg-arc-surface py-section-sm md:py-section"
    >
      <div className="mx-auto flex max-w-3xl flex-col items-center gap-4 px-6 text-center md:px-8">
        <p className="text-eyebrow text-arc-primary-900">404 error</p>
        <h1
          id="redeem-not-found-heading"
          className="text-display-sm md:text-display-md text-arc-gray-900 font-semibold"
        >
          Page not found
        </h1>
        <p className="text-lead text-arc-gray-600">
          We can&apos;t find this page. The link may be wrong, or the page may not be available yet.
        </p>
        <Link
          to="/"
          className="text-arc-primary-900 hover:text-arc-gray-900 text-md inline-flex min-h-11 items-center font-semibold underline underline-offset-4"
        >
          Go to the home page
        </Link>
      </div>
    </section>
  );
}
